import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Grid, Card, Button, Table, Icon, Input, Modal, Popup} from 'semantic-ui-react';
import {headerCellTitlesOfData, headerCellTitlesOfControl} from '../static/commonStructures';
import {hasAccessPermission} from '../../../common/permissions/validations';
import {
  isRowInvalid, getDeepCopyOfBidimensionalArray, truncateData, convertToObject,
  checkRepeatedPrimaryKey, checkFactorPattern, isFactorLessThanOrEqualToZero,
} from '../utils';
import {resources, scopes} from '../../../common/permissions/dictionary';
import {paths} from '../../../common/dictionaries/urls';
import {Actions} from '../../../common/actions';
import ExcelReader from '../../../excelReader/components/index';

const fieldsToDisplay = headerCellTitlesOfData.concat(headerCellTitlesOfControl);
const INITIAL_STATE = {
  'savedPriceFactor': false,
  'tableValues': [],
  'disableSaveButton': true,
};

class AddPriceFactors extends Component {
  constructor(props) {
    super(props);
    this.state = INITIAL_STATE;
    this.readExcelFile = this.readExcelFile.bind(this);
    this.handleSaveClick = this.handleSaveClick.bind(this);
    this.handleCancelClick = this.handleCancelClick.bind(this);
  }

  componentDidMount() {
    if (
      !hasAccessPermission(
        this.props.permissions,
        resources.PriceFactorsMaintenance,
        scopes.Edit
      )
    ) {
      this.props.navigateTo(paths.AccessDenied);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.tableValues !== this.state.tableValues) {
      const {tableValues} = this.state;
      tableValues.some((row) => {
        this.setState({'disableSaveButton': isRowInvalid(row, tableValues)});
        return isRowInvalid(row, tableValues);
      });
    }
    if (prevState.savedPriceFactor !== this.state.savedPriceFactor && this.state.savedPriceFactor) {
      this.props.navigateBack();
    }
  }

  formatValues(tableValues) {
    const parsedValues = [];
    tableValues.forEach((row) => parsedValues.push(convertToObject(row)));
    return parsedValues;
  }

  formatData(data) {
    return data.map((row) => {
      return headerCellTitlesOfData.map((columnDetail, columnIndex) => {
        return truncateData(columnDetail, row[columnIndex]);
      });
    });
  }

  handleCancelClick() {
    this.props.navigateBack();
  }

  async handleSaveClick() {
    const {tableValues} = this.state;
    const parsedValues = this.formatValues(tableValues);
    await this.props.saveParsedPriceFactors(parsedValues);
    this.setState({'savedPriceFactor': true});
  }

  handleChangeValue(event, position, data = null) {
    const {rowIndex, columnIndex} = position;
    const tableValues = this.state.tableValues;
    const updatedTableValues = getDeepCopyOfBidimensionalArray(tableValues);
    const updatedData = data ? data.value : event.target.value;
    updatedTableValues[rowIndex][columnIndex] = updatedData.toUpperCase();
    this.setState({'tableValues': updatedTableValues});
  }

  readExcelFile(data) {
    const {tableValues} = this.state;
    const formattedData = this.formatData(data.slice(1, data.length));
    const updatedTableValues = getDeepCopyOfBidimensionalArray(tableValues);
    this.setState({'tableValues': [...updatedTableValues, ...formattedData]});
  }

  renderErrorMessage(errorKey, isError) {
    const messages = {
      'duplicatedPrimaryKey': 'Product code repetido',
      'invalidFactors': 'El factor debe tener hasta 10 decimales',
      'negativeFactors': 'El factor debe ser mayor que cero',
    };
    return isError ? <div className='invalid-value'>{messages[errorKey]}</div> : null;
  }

  renderInputFields(element, rowIndex) {
    const {tableValues} = this.state;
    const inputFields = [...headerCellTitlesOfData];
    return inputFields.map((columnDetail, columnIndex) => {
      const position = {'rowIndex': rowIndex, 'columnIndex': columnIndex};
      const errors = {
        'duplicatedPrimaryKey': checkRepeatedPrimaryKey(element[columnIndex], tableValues, columnDetail.name),
        'invalidFactors': checkFactorPattern(element[columnIndex], columnDetail.name),
        'negativeFactors': isFactorLessThanOrEqualToZero(element[columnIndex], columnDetail.name),
      };

      return (
        <Table.Cell key={columnIndex} style={{'width': columnDetail.width}}>
          <Input
            error={errors.duplicatedPrimaryKey || errors.invalidFactors}
            maxLength={columnDetail.maxLength}
            required={columnDetail.required}
            style={{'width': columnDetail.width}}
            key={rowIndex.toString().concat(columnIndex)}
            value={element[columnIndex] || ''}
            onChange={(event) => this.handleChangeValue(event, position)}
          />
          {this.renderErrorMessage('duplicatedPrimaryKey', errors.duplicatedPrimaryKey)}
          {this.renderErrorMessage('invalidFactors', errors.invalidFactors)}
          {this.renderErrorMessage('negativeFactors', errors.negativeFactors)}
        </Table.Cell>
      );
    });
  }

  deleteRow(row) {
    const {tableValues} = this.state;

    if (tableValues.length === 1) {
      this.setState(INITIAL_STATE);
      return;
    }
    const updatedTableValues = getDeepCopyOfBidimensionalArray(tableValues);
    updatedTableValues.splice(row, 1);
    this.setState({'tableValues': updatedTableValues});
  }

  addItem() {
    const {tableValues} = this.state;
    const updatedTableValues = getDeepCopyOfBidimensionalArray(tableValues);
    const defaultArray = Array(headerCellTitlesOfData.length);
    defaultArray.fill('');
    this.setState({'tableValues': [...updatedTableValues, defaultArray]});
  }

  renderDeleteAction(row) {
    const INDEX_OF_DELETE_CONTROL = 0;
    const deleteWidth =
      headerCellTitlesOfControl[INDEX_OF_DELETE_CONTROL].width;

    return (
      <Table.Cell textAlign='center' style={{'width': deleteWidth}}>
        <Modal
          size={'mini'}
          trigger={
            <span className='link'>
              <Popup
                trigger={
                  <span>
                    <Icon link name='close' color='red' size='large' />
                  </span>
                }
                content='Eliminar'
                position='top center'
                inverted
              />
            </span>
          }
          header='¿Desea eliminar el item seleccionado?'
          actions={[
            {'key': 'cancel', 'content': 'Cancelar'},
            {
              'key': 'done',
              'content': 'Confirmar',
              'positive': true,
              'onClick': this.deleteRow.bind(this, row),
              'primary': true,
            },
          ]}
        />
      </Table.Cell>
    );
  }

  renderAddItem() {
    return (
      <div className='addItem' onClick={() => this.addItem()}>
        <Icon name='add' />
        <strong>AGREGAR REGISTRO</strong>
      </div>
    );
  }

  renderParsedData() {
    const {tableValues} = this.state;

    if (!tableValues) {
      return null;
    }

    return tableValues.map((element, rowIndex) => {
      const duplicatedPrimaryKey = checkRepeatedPrimaryKey(
        element[0],
        tableValues
      );
      return (
        <Table.Row key={rowIndex} negative={duplicatedPrimaryKey}>
          {this.renderInputFields(element, rowIndex)}
          {this.renderDeleteAction(rowIndex)}
        </Table.Row>
      );
    });
  }

  renderTableHeaderCells() {
    return fieldsToDisplay.map((element) => {
      return (
        <Table.HeaderCell key={`header${element.name}`} textAlign='center'>
          {element.name}
        </Table.HeaderCell>
      );
    });
  }

  renderTable() {
    return (
      <Table striped className='toyota-grid'>
        <Table.Header>
          <Table.Row>{this.renderTableHeaderCells()}</Table.Row>
        </Table.Header>
        <Table.Body>{this.renderParsedData()}</Table.Body>
      </Table>
    );
  }

  renderTitleAndActions() {
    const {disableSaveButton} = this.state;
    return (
      <Card raised fluid>
        <Grid.Row>
          <div className='title-bar'>
            <div className='title'>
              <Icon name='arrow left' onClick={this.handleCancelClick} />
              <h2>Creación de factores de precio</h2>
              <div className='title-bar-actions'>
                <ExcelReader onGetFile={this.readExcelFile} />
                <Button
                  primary
                  disabled={disableSaveButton}
                  onClick={this.handleSaveClick}
                >
                  Guardar
                </Button>
              </div>
            </div>
          </div>
        </Grid.Row>
      </Card>
    );
  }

  render() {
    return (
      <div className='price-factors'>
        {this.renderTitleAndActions()}
        <Card fluid raised>
          <div className='horizontal-scroll'>{this.renderTable()}</div>
          {this.renderAddItem()}
        </Card>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    'permissions': state.session.permissions,
  };
};

const {
  navigateTo,
  navigateBack,
  saveParsedPriceFactors,
} = Actions;

export default connect(mapStateToProps, {navigateTo, navigateBack, saveParsedPriceFactors})(
  AddPriceFactors
);
